import { CdkScrollable } from '@angular/cdk/scrolling';
import { Component, effect, Inject, OnInit, viewChild } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogActions, MatDialogContent, MatDialogRef, MatDialogTitle } from '@angular/material/dialog';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { Address, Zone } from '@domains';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import {
  Destructible,
  DisableDoubleClickDirective,
  FxFlexDirective,
  FxLayoutAlignDirective,
  FxLayoutDirective,
  InfoBoxComponent,
} from '@rspl-ui';
import { LaddaModule } from 'angular2-ladda';
import { takeUntil } from 'rxjs/operators';

import { MapComponent } from '../../../../../../map/src/lib/map/map.component';
import { AppState } from '../../../store/app-state.interface';
import {
  createZone,
  createZoneFail,
  createZoneSuccess,
  updateZone,
  updateZoneFail,
  updateZoneSuccess,
} from '../../../store/zone/zone.actions';
import { ZipPickerComponent } from '../../zip-picker/zip-picker.component';

export interface SaveZoneData {
  title: string;
  parentId: string;
  zone: Zone;
  address: Address;
}

@Component({
  selector: 'app-save-zone-dialog',
  templateUrl: './save-zone-dialog.component.html',
  styleUrls: ['./save-zone-dialog.component.scss'],
  imports: [
    MatDialogTitle,
    CdkScrollable,
    MatDialogContent,
    MatFormField,
    FxFlexDirective,
    MatLabel,
    MatInput,
    FormsModule,
    MatDialogActions,
    FxLayoutDirective,
    FxLayoutAlignDirective,
    MatButton,
    DisableDoubleClickDirective,
    ReactiveFormsModule,
    ZipPickerComponent,
    MatSlideToggle,
    MapComponent,
    InfoBoxComponent,
    LaddaModule,
  ],
})
export class SaveZoneDialogComponent extends Destructible implements OnInit {
  readonly map = viewChild<MapComponent>(MapComponent);

  form: FormGroup<{
    name: FormControl<string>;
    description: FormControl<string>;
    zips: FormControl<string[]>;
    hasParent: FormControl<boolean>;
  }>;

  isSaving = false;

  public constructor(
    public reference: MatDialogRef<SaveZoneDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: SaveZoneData,
    private store: Store<AppState>,
    private actions: Actions,
  ) {
    super();
    effect(() => {
      this.map()?.setAddress(this.data.address, false);
    });
    this.form = new FormGroup({
      name: new FormControl(this.data.zone.name, Validators.required),
      description: new FormControl(this.data.zone.description),
      zips: new FormControl([...this.data.zone.zips], Validators.required),
      hasParent: new FormControl(!!(this.data.zone.store_id || this.data.zone.vehicle_id)),
    });
  }

  ngOnInit(): void {
    this.actions.pipe(ofType(createZoneSuccess, updateZoneSuccess), takeUntil(this.destroy$)).subscribe((res) => this.close(res.zone));
    this.actions.pipe(ofType(createZoneFail, updateZoneFail), takeUntil(this.destroy$)).subscribe(() => (this.isSaving = false));
  }

  save(): void {
    this.form.updateValueAndValidity();
    this.form.markAllAsTouched();
    if (this.form.invalid) return;
    this.isSaving = true;
    if (this.data.zone.id) {
      this.store.dispatch(
        updateZone({
          zone: new Zone({
            ...this.data.zone,
            ...this.form.getRawValue(),
            ...(this.form.get('hasParent').value
              ? this.data.zone.charityId
                ? { store_id: this.data.parentId }
                : { vehicle_id: this.data.parentId }
              : this.data.zone.charityId
                ? { store_id: null }
                : { vehicle_id: null }),
          }),
        }),
      );
    } else {
      this.store.dispatch(
        createZone({
          zone: new Zone({
            ...this.data.zone,
            ...this.form.getRawValue(),
            ...(this.form.get('hasParent').value
              ? this.data.zone.charityId
                ? { store_id: this.data.parentId }
                : { vehicle_id: this.data.parentId }
              : this.data.zone.charityId
                ? { store_id: null }
                : { vehicle_id: null }),
          }),
        }),
      );
    }
  }

  public close(res?: Zone): void {
    this.reference.close(res);
  }

  get hasParent(): FormControl<boolean> {
    return this.form.get('hasParent') as FormControl<boolean>;
  }
}
